'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Map as MapIcon, Sliders, Ship, ChevronDown } from 'lucide-react';

export default function SearchGuideAccordion() {
  const t = useTranslations('homePage');
  const [isOpen, setIsOpen] = useState(false);
  
  // On desktop (lg), always show as expanded
  const isDesktopExpanded = true;

  // Parse the translation strings to extract title and description
  const parseStep = (stepText: string) => {
    const parts = stepText.split('. ');
    return {
      title: parts[0],
      description: parts.slice(1).join('. '),
    };
  };

  const step1 = parseStep(t('searchGuideStep1'));
  const step2 = parseStep(t('searchGuideStep2'));
  const step3 = parseStep(t('searchGuideStep3'));

  const steps = [
    {
      icon: MapIcon,
      iconColor: 'bg-blue-600',
      iconShadow: 'shadow-blue-600/50',
      iconHoverShadow: 'hover:shadow-blue-600/70',
      ...step1,
    },
    {
      icon: Sliders,
      iconColor: 'bg-purple-600',
      iconShadow: 'shadow-purple-600/50',
      iconHoverShadow: 'hover:shadow-purple-600/70',
      ...step2,
    },
    {
      icon: Ship,
      iconColor: 'bg-green-600',
      iconShadow: 'shadow-green-600/50',
      iconHoverShadow: 'hover:shadow-green-600/70',
      ...step3,
    },
  ];

  return (
    <div className="w-full max-w-7xl mx-auto">
      <section className="w-full bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xl">
        {/* Accordion Header - Hidden on desktop, visible on mobile */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden w-full py-6 px-6 md:px-8 flex items-center justify-between text-left hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-200 group relative"
          aria-expanded={isOpen}
          aria-controls="search-guide-content"
        >
          <h5 className="text-2xl font-bold text-slate-900 dark:text-white relative z-10">
            {t('searchGuideTitle')}
          </h5>
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 group-hover:bg-slate-200 dark:group-hover:bg-slate-700 transition-all duration-200 relative z-10">
            <ChevronDown
              className={`w-5 h-5 text-slate-900 dark:text-white transition-transform duration-300 ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </div>
          {/* Background glow effect when collapsed */}
          {!isOpen && (
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-green-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          )}
        </button>

        {/* Static Header for Desktop */}
        <div className="hidden lg:block py-6 px-6 md:px-8">
          <h5 className="text-2xl font-bold text-slate-900 dark:text-white">
            {t('searchGuideTitle')}
          </h5>
        </div>

        {/* Accordion Content - Always expanded on desktop, collapsible on mobile */}
        <div
          id="search-guide-content"
          className={`lg:!max-h-none lg:!opacity-100 overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen || isDesktopExpanded
              ? 'max-h-[600px] opacity-100' 
              : 'max-h-0 opacity-0'
          }`}
          style={{
            transitionProperty: 'max-height, opacity',
          }}
        >
          <div className="px-8 py-12 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
            {/* Desktop: 3 columns grid */}
            <div className="hidden lg:grid lg:grid-cols-3 lg:gap-12">
              {steps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div
                    key={index}
                    className="flex flex-col items-center text-center"
                  >
                    {/* Icon with fixed dimensions */}
                    <div
                      className={`w-20 h-20 flex items-center justify-center ${step.iconColor} rounded-full text-white mb-6 shadow-lg ${step.iconShadow} transform transition-all duration-300 hover:scale-110 ${step.iconHoverShadow}`}
                    >
                      <IconComponent size={32} />
                    </div>
                    
                    {/* Title */}
                    <h6 className="text-lg font-bold text-slate-900 dark:text-white mb-3 max-w-sm">
                      {step.title}
                    </h6>
                    
                    {/* Description */}
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-sm">
                      {step.description}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Mobile: Vertical stack */}
            <div className="flex flex-col gap-8 lg:hidden">
              {steps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div
                    key={index}
                    className="flex flex-col items-center text-center px-4"
                  >
                    {/* Icon with fixed dimensions */}
                    <div
                      className={`w-20 h-20 flex items-center justify-center ${step.iconColor} rounded-full text-white mb-4 shadow-lg ${step.iconShadow} transform transition-all duration-300 hover:scale-110 ${step.iconHoverShadow}`}
                    >
                      <IconComponent size={32} />
                    </div>
                    
                    {/* Title */}
                    <h6 className="text-lg font-bold text-slate-900 dark:text-white mb-2 max-w-sm">
                      {step.title}
                    </h6>
                    
                    {/* Description */}
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-sm">
                      {step.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

